import { useState, useEffect, useCallback } from "react";
import "./Crew.css";
import ICAgreementGate from "./components/ICAgreementGate";

const BACKEND_URL = "";

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(u => { setUser(u); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  return { user, loading };
};

/**
 * NotificationBell — silent in-app inbox. Polls every 60s so newly-assigned
 * jobs surface even without SMS. Zero configuration.
 */
const NotificationBell = () => {
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/crew/notifications`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setRows(d.rows || []); setUnread(d.unread_count || 0); } })
      .catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 180_000);
    return () => clearInterval(t);
  }, [load]);
  const markAllRead = () => {
    fetch(`${BACKEND_URL}/api/crew/notifications/mark-all`, {
      method: "POST", credentials: "include",
    }).then(() => load());
  };
  const jump = (n) => {
    fetch(`${BACKEND_URL}/api/crew/notifications/${n.notification_id}/read`, {
      method: "POST", credentials: "include",
    }).then(() => {
      if (n.deep_link) window.location.assign(n.deep_link);
      else load();
    });
  };
  return (
    <div style={{ position: "relative", marginLeft: 12 }} data-testid="crew-notification-bell">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="crew-notification-toggle"
        style={{
          position: "relative",
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#F3F4F6",
          padding: "8px 12px",
          borderRadius: 999,
          cursor: "pointer",
          fontSize: 14,
        }}
        title={`${unread} unread notification${unread === 1 ? "" : "s"}`}
      >
        🔔
        {unread > 0 && (
          <span data-testid="crew-notification-badge" style={{
            position: "absolute", top: -4, right: -4,
            background: "#ef4444", color: "#fff",
            fontSize: 10, fontWeight: 800,
            padding: "2px 6px", borderRadius: 999,
            minWidth: 18, textAlign: "center",
          }}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="crew-notification-panel" style={{
          position: "absolute", right: 0, top: 44,
          width: 320, maxHeight: 420, overflowY: "auto",
          background: "#0f172a",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 10, padding: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          zIndex: 5000,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "6px 8px 8px", borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}>
            <strong style={{ color: "#F3F4F6", fontSize: 13 }}>Notifications</strong>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="crew-notification-mark-all"
                      style={{ background: "transparent", border: 0, color: "#00CFCF", cursor: "pointer", fontSize: 11 }}>
                Mark all read
              </button>
            )}
          </div>
          {rows.length === 0 ? (
            <div style={{ color: "#94A3B8", fontSize: 12, padding: 12 }}>
              No notifications yet.
            </div>
          ) : rows.slice(0, 20).map(n => (
            <div key={n.notification_id}
                 data-testid={`crew-notification-${n.notification_id}`}
                 onClick={() => jump(n)}
                 style={{
                   padding: "10px 8px",
                   borderBottom: "1px solid rgba(255,255,255,0.06)",
                   cursor: "pointer",
                   background: n.read ? "transparent" : "rgba(0,207,207,0.08)",
                 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: n.read ? "#94A3B8" : "#F3F4F6" }}>
                {n.title}
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>
                {n.body.slice(0, 100)}{n.body.length > 100 ? "…" : ""}
              </div>
              <div style={{ fontSize: 10, color: "#64748B", marginTop: 4 }}>
                {new Date(n.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PHOTO_TYPES = [
  { value: "before", label: "Before", desc: "Initial condition before work" },
  { value: "issue", label: "Issue Found", desc: "Problem discovered during inspection" },
  { value: "issue-fixed", label: "Issue Fixed", desc: "Problem has been resolved" },
  { value: "progress", label: "Progress", desc: "Work in progress" },
  { value: "completed", label: "Completed", desc: "Work finished on this area" },
  { value: "signoff", label: "Sign-Off", desc: "Final documentation for approval" }
];

const CrewProject = ({ project, onBack }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoType, setPhotoType] = useState("before");
  const [photoNotes, setPhotoNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [photoFilter, setPhotoFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("photos");
  const [insurance, setInsurance] = useState(null);
  const [insuranceForm, setInsuranceForm] = useState({ insurance_company: "", policy_number: "", coverage_amount: "", expiration_date: "" });
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [savingInsurance, setSavingInsurance] = useState(false);

  const loadDetail = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/crew/projects/${project.project_id}`, { credentials: "include" });
    if (r.ok) setDetail(await r.json());
    setLoading(false);
  }, [project.project_id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  useEffect(() => {
    const loadInsurance = async () => {
      const r = await fetch(`${BACKEND_URL}/api/crew/insurance`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setInsurance(data);
        if (data.insurance_company) {
          setInsuranceForm({
            insurance_company: data.insurance_company || "",
            policy_number: data.policy_number || "",
            coverage_amount: data.coverage_amount || "",
            expiration_date: data.expiration_date || ""
          });
        }
      }
    };
    loadInsurance();
  }, []);

  const saveInsurance = async () => {
    setSavingInsurance(true);
    const r = await fetch(`${BACKEND_URL}/api/crew/insurance`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(insuranceForm)
    });
    if (r.ok) setInsurance(await r.json());
    if (insuranceFile) {
      const fd = new FormData();
      fd.append("file", insuranceFile);
      const ur = await fetch(`${BACKEND_URL}/api/crew/insurance/upload`, { method: "POST", credentials: "include", body: fd });
      if (ur.ok) { setInsurance(await ur.json()); setInsuranceFile(null); }
    }
    setSavingInsurance(false);
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    setUploading(true);
    setUploadSuccess("");
    const formData = new FormData();
    formData.append("file", photoFile);
    formData.append("photo_type", photoType);
    formData.append("notes", photoNotes);
    try {
      const r = await fetch(`${BACKEND_URL}/api/crew/projects/${project.project_id}/photos`, {
        method: "POST", credentials: "include", body: formData
      });
      if (r.ok) {
        setPhotoFile(null);
        setPhotoNotes("");
        setUploadSuccess("Photo uploaded successfully");
        setTimeout(() => setUploadSuccess(""), 3000);
        loadDetail();
      }
    } catch (e) { console.error("Upload failed:", e); }
    setUploading(false);
  };

  const toggleMaterial = async (index) => {
    await fetch(`${BACKEND_URL}/api/crew/projects/${project.project_id}/materials/${index}`, {
      method: "PUT", credentials: "include"
    });
    loadDetail();
  };

  const [cleaningItems, setCleaningItems] = useState(null);

  useEffect(() => {
    const loadCleaning = async () => {
      const r = await fetch(`${BACKEND_URL}/api/crew/projects/${project.project_id}/cleaning-checklist`, { credentials: "include" });
      if (r.ok) setCleaningItems(await r.json());
    };
    loadCleaning();
  }, [project.project_id]);

  const toggleCleaningItem = async (index) => {
    await fetch(`${BACKEND_URL}/api/crew/projects/${project.project_id}/cleaning-checklist/${index}`, { method: "PUT", credentials: "include" });
    const r = await fetch(`${BACKEND_URL}/api/crew/projects/${project.project_id}/cleaning-checklist`, { credentials: "include" });
    if (r.ok) setCleaningItems(await r.json());
  };

  const getPhotoUrl = (photo) => `${BACKEND_URL}/api/files/${photo.storage_path}`;

  if (loading) return <div className="crew-loading">Loading project...</div>;
  if (!detail) return <div className="crew-empty">Project not found</div>;

  const photos = detail.photos || [];
  const materials = detail.materials || [];
  const filteredPhotos = photoFilter === "all" ? photos : photos.filter(p => p.photo_type === photoFilter);
  const materialsChecked = materials.filter(m => m.checked).length;

  return (
    <div className="crew-project" data-testid="crew-project-detail">
      <button onClick={onBack} className="crew-back-btn" data-testid="crew-back-btn">&larr; My Projects</button>

      <div className="crew-project-header">
        <h2>{detail.address}{detail.unit_number ? ` #${detail.unit_number}` : ""}</h2>
        <div className="crew-project-meta">
          <span className={`crew-phase-badge phase-${detail.phase || "intake"}`}>{detail.phase || "Intake"}</span>
          <span className="crew-status">{detail.status}</span>
        </div>
        {detail.address && (
          <div className="crew-directions-row" data-testid="crew-directions-row" style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <a
              data-testid="crew-directions-gmaps"
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(detail.address + (detail.unit_number ? " " + detail.unit_number : ""))}&travelmode=driving`}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#22c55e", color: "#052e16",
                padding: "10px 14px", borderRadius: 8, textDecoration: "none",
                fontWeight: 800, fontSize: 13, letterSpacing: "0.04em",
              }}
            >
              🧭 Directions (Google Maps)
            </a>
            <a
              data-testid="crew-directions-apple"
              href={`https://maps.apple.com/?daddr=${encodeURIComponent(detail.address + (detail.unit_number ? " " + detail.unit_number : ""))}`}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "transparent", color: "#F3F4F6",
                border: "1px solid rgba(255,255,255,0.2)",
                padding: "10px 14px", borderRadius: 8, textDecoration: "none",
                fontWeight: 700, fontSize: 12, letterSpacing: "0.04em",
              }}
            >
              🍎 Apple Maps
            </a>
            <a
              data-testid="crew-call-property"
              href={`tel:8173818831`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "transparent", color: "#00CFCF",
                border: "1px solid rgba(0,207,207,0.4)",
                padding: "10px 14px", borderRadius: 8, textDecoration: "none",
                fontWeight: 700, fontSize: 12, letterSpacing: "0.04em",
              }}
            >
              📞 Call COO
            </a>
          </div>
        )}
      </div>

      <div className="crew-tabs" data-testid="crew-tabs">
        <button className={`crew-tab ${activeTab === "photos" ? "active" : ""}`} onClick={() => setActiveTab("photos")} data-testid="crew-tab-photos">
          Photos ({photos.length})
        </button>
        <button className={`crew-tab ${activeTab === "materials" ? "active" : ""}`} onClick={() => setActiveTab("materials")} data-testid="crew-tab-materials">
          Materials {materials.length > 0 ? `(${materialsChecked}/${materials.length})` : ""}
        </button>
        {cleaningItems?.items?.length > 0 && (
          <button className={`crew-tab ${activeTab === "cleaning" ? "active" : ""}`} onClick={() => setActiveTab("cleaning")} data-testid="crew-tab-cleaning">
            Cleaning ({cleaningItems.items.filter(i => i.checked).length}/{cleaningItems.items.length})
          </button>
        )}
        <button className={`crew-tab ${activeTab === "insurance" ? "active" : ""}`} onClick={() => setActiveTab("insurance")} data-testid="crew-tab-insurance">
          Insurance {insurance?.status === "submitted" ? "\u2713" : "!"}
        </button>
      </div>

      {activeTab === "photos" && (
        <div className="crew-photos-section" data-testid="crew-photos-section">
          <div className="crew-upload-card" data-testid="crew-upload-card">
            <h3>Upload Photo</h3>

            <label className="crew-file-label" data-testid="crew-file-label">
              {photoFile ? photoFile.name : "Tap to select photo"}
              <input type="file" accept="image/*" capture="environment" onChange={e => setPhotoFile(e.target.files[0])} className="crew-file-input" data-testid="crew-file-input" />
            </label>

            <div className="crew-type-grid" data-testid="crew-type-grid">
              {PHOTO_TYPES.map(pt => (
                <button key={pt.value} className={`crew-type-btn ${photoType === pt.value ? "active" : ""} type-${pt.value}`} onClick={() => setPhotoType(pt.value)} data-testid={`crew-type-${pt.value}`}>
                  <span className="crew-type-label">{pt.label}</span>
                  <span className="crew-type-desc">{pt.desc}</span>
                </button>
              ))}
            </div>

            <textarea value={photoNotes} onChange={e => setPhotoNotes(e.target.value)} placeholder="Documentation notes — describe what you see, what was fixed, any issues..." className="crew-notes-input" data-testid="crew-notes-input" />

            <button onClick={uploadPhoto} disabled={!photoFile || uploading} className="crew-upload-btn" data-testid="crew-upload-btn">
              {uploading ? "Uploading..." : "Upload Photo"}
            </button>

            {uploadSuccess && <div className="crew-upload-success" data-testid="crew-upload-success">{uploadSuccess}</div>}
          </div>

          {photos.length > 0 && (
            <>
              <div className="crew-filter-row" data-testid="crew-photo-filters">
                {["all", ...PHOTO_TYPES.map(pt => pt.value)].map(f => {
                  const count = f === "all" ? photos.length : photos.filter(p => p.photo_type === f).length;
                  if (f !== "all" && count === 0) return null;
                  return (
                    <button key={f} className={`crew-filter-btn ${photoFilter === f ? "active" : ""}`} onClick={() => setPhotoFilter(f)} data-testid={`crew-filter-${f}`}>
                      {f === "all" ? "All" : PHOTO_TYPES.find(pt => pt.value === f)?.label || f} ({count})
                    </button>
                  );
                })}
              </div>
              <div className="crew-photo-grid" data-testid="crew-photo-grid">
                {filteredPhotos.map(photo => {
                  const isVideo = photo.is_video || /\.(mp4|mov|webm|m4v)$/i.test(photo.original_filename || "");
                  return (
                    <div key={photo.photo_id} className="crew-photo-card" data-testid={`crew-photo-${photo.photo_id}`}>
                      <div className="crew-photo-img">
                        {isVideo ? (
                          <video src={getPhotoUrl(photo)} controls preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }} />
                        ) : (
                          <img src={getPhotoUrl(photo)} alt={photo.notes || photo.photo_type} loading="lazy" />
                        )}
                        <span className={`crew-badge badge-${photo.photo_type}`}>{photo.photo_type.replace("-", " ")}{isVideo ? " · video" : ""}</span>
                      </div>
                      <div className="crew-photo-info">
                        {photo.notes && <p className="crew-photo-notes">{photo.notes}</p>}
                        <span className="crew-photo-meta">{new Date(photo.created_at).toLocaleString()} — {photo.uploaded_by}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "materials" && (
        <div className="crew-materials-section" data-testid="crew-materials-section">
          <div data-testid="crew-scope-lock-banner" className="mf-training-tip" style={{ background: "rgba(13, 148, 136, 0.12)", border: "1px solid rgba(13, 148, 136, 0.45)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>🔒</span>
              <strong style={{ color: "#0d9488", fontSize: 13 }}>Locked Scope — Verify Before Work Begins</strong>
            </div>
            <p style={{ fontSize: 11.5, color: "#64748b", margin: "6px 0 0", lineHeight: 1.4 }}>
              Each item below must match the client's signed Scope-Lock Agreement. If anything differs, STOP and notify Shannon/Shaneka before proceeding. Do not substitute materials without written approval.
            </p>
          </div>
          <h3>Materials Checklist</h3>
          <p className="crew-materials-note">Check off each material as verified on-site. All checks are timestamped.</p>
          {materials.length === 0 ? (
            <div className="crew-empty-materials">No materials assigned to this project yet. Admin will add the materials list from the Dashboard.</div>
          ) : (
            <>
              <div className="crew-materials-progress">
                <div className="crew-progress-bar">
                  <div className="crew-progress-fill" style={{ width: `${materials.length > 0 ? (materialsChecked / materials.length) * 100 : 0}%` }}></div>
                </div>
                <span className="crew-progress-text">{materialsChecked} of {materials.length} verified</span>
              </div>
              <div className="crew-materials-list" data-testid="crew-materials-list">
                {materials.map((item, index) => (
                  <button key={index} className={`crew-material-item ${item.checked ? "checked" : ""}`} onClick={() => toggleMaterial(index)} data-testid={`crew-material-${index}`}>
                    <span className={`crew-check ${item.checked ? "checked" : ""}`}>{item.checked ? "\u2713" : ""}</span>
                    <div className="crew-material-info">
                      <span className="crew-material-name">{item.name}</span>
                      {item.spec && <span className="crew-material-spec">{item.spec}</span>}
                      {item.quantity && <span className="crew-material-qty">Qty: {item.quantity}</span>}
                    </div>
                    {item.checked && item.checked_by && (
                      <span className="crew-checked-meta">{item.checked_by} — {new Date(item.checked_at).toLocaleTimeString()}</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "cleaning" && cleaningItems?.items?.length > 0 && (
        <div className="crew-materials-section" data-testid="crew-cleaning-section">
          <h3>Cleaning Checklist</h3>
          <p className="crew-materials-note">Check off each cleaning task as completed on-site. All checks are timestamped and visible to admin and client.</p>
          <div className="crew-materials-progress">
            <div className="crew-progress-bar">
              <div className="crew-progress-fill" style={{ width: `${(cleaningItems.items.filter(i => i.checked).length / cleaningItems.items.length) * 100}%` }}></div>
            </div>
            <span className="crew-progress-text">{cleaningItems.items.filter(i => i.checked).length} of {cleaningItems.items.length} completed</span>
          </div>
          {["Kitchen", "Bathrooms", "Floors", "General", "Final"].map(cat => {
            const catItems = cleaningItems.items.map((item, idx) => ({...item, _idx: idx})).filter(i => i.category === cat);
            if (catItems.length === 0) return null;
            return (
              <div key={cat} className="crew-cleaning-category">
                <div className="crew-cleaning-cat-header">{cat} ({catItems.filter(i => i.checked).length}/{catItems.length})</div>
                <div className="crew-materials-list">
                  {catItems.map(item => (
                    <button key={item._idx} className={`crew-material-item ${item.checked ? "checked" : ""}`} onClick={() => toggleCleaningItem(item._idx)} data-testid={`crew-cleaning-${item._idx}`}>
                      <span className={`crew-check ${item.checked ? "checked" : ""}`}>{item.checked ? "\u2713" : ""}</span>
                      <div className="crew-material-info">
                        <span className="crew-material-name">{item.item}</span>
                      </div>
                      {item.checked && item.checked_by && (
                        <span className="crew-checked-meta">{item.checked_by} — {new Date(item.checked_at).toLocaleTimeString()}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "insurance" && (
        <div className="crew-insurance-section" data-testid="crew-insurance-section">
          <h3>Your Insurance Information</h3>
          <p className="crew-insurance-note">As a 1099 independent contractor, you are required to maintain current insurance. Your insurance info is visible to property owners and brokers on every project you're assigned to.</p>

          <div className="crew-insurance-warning" data-testid="insurance-liability-notice">
            <strong>1099 Damage Liability Notice</strong>
            <p>As an independent contractor, any damages caused to the property or its contents during the course of work are your responsibility and will be filed against YOUR insurance policy — not Moovin Forward's. Maintain adequate coverage at all times.</p>
          </div>

          <div className="crew-insurance-form" data-testid="crew-insurance-form">
            <div className="ins-field">
              <label>Insurance Company</label>
              <input value={insuranceForm.insurance_company} onChange={e => setInsuranceForm(p => ({...p, insurance_company: e.target.value}))} placeholder="e.g., State Farm, Progressive, GEICO" className="crew-ins-input" data-testid="ins-company" />
            </div>
            <div className="ins-field">
              <label>Policy Number</label>
              <input value={insuranceForm.policy_number} onChange={e => setInsuranceForm(p => ({...p, policy_number: e.target.value}))} placeholder="e.g., GL-2026-4521890" className="crew-ins-input" data-testid="ins-policy" />
            </div>
            <div className="ins-field">
              <label>Coverage Amount</label>
              <input value={insuranceForm.coverage_amount} onChange={e => setInsuranceForm(p => ({...p, coverage_amount: e.target.value}))} placeholder="e.g., $500,000 General Liability" className="crew-ins-input" data-testid="ins-coverage" />
            </div>
            <div className="ins-field">
              <label>Expiration Date</label>
              <input type="date" value={insuranceForm.expiration_date} onChange={e => setInsuranceForm(p => ({...p, expiration_date: e.target.value}))} className="crew-ins-input" data-testid="ins-expiration" />
            </div>
            <div className="ins-field">
              <label>Certificate of Insurance (COI) — PDF or Image</label>
              <label className="crew-file-label ins-file-label">
                {insuranceFile ? insuranceFile.name : (insurance?.coi_filename ? `Current: ${insurance.coi_filename}` : "Upload COI document")}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setInsuranceFile(e.target.files[0])} className="crew-file-input" data-testid="ins-coi-upload" />
              </label>
            </div>
            <button onClick={saveInsurance} disabled={savingInsurance || !insuranceForm.insurance_company || !insuranceForm.policy_number} className="crew-upload-btn" data-testid="ins-save-btn">
              {savingInsurance ? "Saving..." : "Save Insurance Info"}
            </button>
          </div>

          {insurance?.status === "submitted" && (
            <div className="crew-insurance-confirmed" data-testid="ins-confirmed">
              Insurance on file: {insurance.insurance_company} — Policy #{insurance.policy_number} — Expires {insurance.expiration_date}
              {insurance.has_coi && " — COI uploaded"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CrewPortal = () => {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCheckin, setActiveCheckin] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkins, setCheckins] = useState([]);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [icAgreementOK, setIcAgreementOK] = useState(false);
  const [icAgreementLoaded, setIcAgreementLoaded] = useState(false);

  // Check IC agreement status once user is loaded
  useEffect(() => {
    if (!user) { setIcAgreementLoaded(true); return; }
    fetch(`${BACKEND_URL}/api/crew/agreement`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { accepted: false })
      .then(d => { setIcAgreementOK(!!d.accepted); setIcAgreementLoaded(true); })
      .catch(() => setIcAgreementLoaded(true));
  }, [user]);

  // Detect if already installed as PWA
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  const loadProjects = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/crew/projects`, { credentials: "include" });
    if (r.ok) setProjects(await r.json());
    setLoading(false);
  }, []);

  const loadCheckins = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/crew/my-checkins`, { credentials: "include" });
    if (r.ok) {
      const data = await r.json();
      setCheckins(data);
      const active = data.find(c => c.status === "on_site");
      if (active) setActiveCheckin(active);
    }
  }, []);

  useEffect(() => {
    if (user) { loadProjects(); loadCheckins(); }
    else setLoading(false);
  }, [user, loadProjects, loadCheckins]);

  useEffect(() => {
    if (user && !localStorage.getItem("mf_crew_disclaimer_seen")) {
      setShowDisclaimer(true);
    }
    // Show install banner if not already installed and not dismissed
    if (user && !isStandalone && !localStorage.getItem("mf_crew_install_dismissed")) {
      setShowInstallBanner(true);
    }
  }, [user, isStandalone]);

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      err => reject(err),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

  const handleCheckIn = async (project) => {
    setCheckingIn(true);
    try {
      const loc = await getLocation();
      const r = await fetch(`${BACKEND_URL}/api/crew/check-in`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ project_id: project.project_id, project_address: project.address || project.name, ...loc })
      });
      if (r.ok) {
        const data = await r.json();
        setActiveCheckin(data);
      }
    } catch (err) {
      alert("Location access required for check-in. Please enable GPS.");
    }
    setCheckingIn(false);
  };

  const handleCheckOut = async () => {
    if (!activeCheckin) return;
    try {
      const loc = await getLocation();
      await fetch(`${BACKEND_URL}/api/crew/check-out`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ checkin_id: activeCheckin.checkin_id, ...loc })
      });
      setActiveCheckin(null);
      loadCheckins();
    } catch {
      await fetch(`${BACKEND_URL}/api/crew/check-out`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ checkin_id: activeCheckin.checkin_id })
      });
      setActiveCheckin(null);
      loadCheckins();
    }
  };

  const dismissDisclaimer = () => {
    setShowDisclaimer(false);
    localStorage.setItem("mf_crew_disclaimer_seen", "1");
  };

  if (authLoading) return <div className="crew-container"><div className="crew-loading">Loading...</div></div>;

  if (!user) {
    return (
      <div className="crew-container" data-testid="crew-login">
        <div className="crew-login-card">
          <h1>Crew Portal</h1>
          <p>Sign in to view your assigned projects, upload inspection photos, and verify materials.</p>
          <button
            type="button"
            onClick={() => {
              // Emergent-managed Google Auth — same redirect Dashboard.handleLogin uses.
              // Fixes the dead <a href="/api/auth/google"> link (404 — endpoint never existed).
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="crew-google-btn"
            data-testid="crew-google-login"
          >
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (user.role !== "crew" && user.role !== "team" && user.role !== "admin") {
    return (
      <div className="crew-container" data-testid="crew-no-access">
        <div className="crew-login-card">
          <h1>Access Pending</h1>
          <p>Your account ({user.email}) hasn't been assigned crew access yet. Contact admin to get your role updated.</p>
        </div>
      </div>
    );
  }

  // Gate: 1099 Independent Contractor Agreement (court-defensible) — required for crew/team.
  // Admins bypass since they're employees/owners, not 1099 contractors.
  if (icAgreementLoaded && !icAgreementOK && user.role !== "admin") {
    return (
      <div className="crew-container" data-testid="crew-ic-gate">
        <div className="crew-login-card" style={{ opacity: 0.4 }}>
          <h1>Crew Portal</h1>
          <p>Loading…</p>
        </div>
        <ICAgreementGate onAccepted={() => setIcAgreementOK(true)} />
      </div>
    );
  }

  if (loading) return <div className="crew-container"><div className="crew-loading">Loading projects...</div></div>;

  if (selected) {
    const proj = projects.find(p => p.project_id === selected);
    if (proj) return <div className="crew-container"><CrewProject project={proj} onBack={() => { setSelected(null); loadProjects(); }} /></div>;
  }

  return (
    <div className="crew-container" data-testid="crew-portal">
      {/* 1099 Device & Communication Disclaimer */}
      {showDisclaimer && (
        <div className="crew-disclaimer-overlay" data-testid="crew-disclaimer">
          <div className="crew-disclaimer-modal">
            <h2>Independent Contractor Agreement — Device & Communication Policy</h2>
            <div className="crew-disclaimer-text">
              <p><strong>PERSONAL DEVICE POLICY</strong></p>
              <p>As an independent contractor (1099) engaged by Moovin Forward, you acknowledge and agree to the following:</p>
              <p>All independent contractors are required to maintain a personal mobile device with reliable cellular service and internet connectivity as a condition of engagement. This device is necessary for:</p>
              <ul>
                <li>GPS-based check-in and check-out at job sites</li>
                <li>Before and after photo documentation of units</li>
                <li>Communication with Moovin Forward management and clients</li>
                <li>Accessing the Crew Portal for project information, materials checklists, and scheduling</li>
              </ul>
              <p><strong>Moovin Forward does not provide, reimburse, or subsidize the cost of personal mobile devices, cellular service plans, data plans, or any related expenses.</strong> The use of your personal device for business communication and documentation is an ordinary and necessary business expense of your independent contracting business and may be deductible on your tax return. Consult your tax professional for guidance.</p>
              <p>By continuing to use the Crew Portal, you acknowledge that you have read, understood, and agree to this policy.</p>
            </div>
            <button onClick={dismissDisclaimer} className="crew-disclaimer-btn" data-testid="crew-disclaimer-accept">I Understand — Continue</button>
          </div>
        </div>
      )}

      <div className="crew-header">
        <h1>My Projects</h1>
        <span className="crew-user">{user.name}</span>
        <NotificationBell />
      </div>

      {/* Add to Home Screen Banner */}
      {showInstallBanner && !isStandalone && (
        <div className="crew-install-banner" data-testid="crew-install-banner">
          <div className="crew-install-content">
            <strong>Add Moovin Forward to Your Home Screen</strong>
            {isIOS ? (
              <p>Tap the <span className="crew-install-icon">Share</span> button at the bottom of Safari, then tap <strong>"Add to Home Screen"</strong>. This gives you one-tap access — no app store needed.</p>
            ) : isAndroid ? (
              <p>Tap the <span className="crew-install-icon">3 dots</span> menu at the top right of Chrome, then tap <strong>"Add to Home Screen"</strong> or <strong>"Install App"</strong>. Opens full-screen like a real app.</p>
            ) : (
              <p>In your browser menu, look for <strong>"Add to Home Screen"</strong> or <strong>"Install"</strong>. This puts an icon on your phone for one-tap access.</p>
            )}
          </div>
          <button onClick={() => { setShowInstallBanner(false); localStorage.setItem("mf_crew_install_dismissed", "1"); }} className="crew-install-dismiss" data-testid="install-dismiss">Got It</button>
        </div>
      )}

      {/* Active Check-In Banner */}
      {activeCheckin && (
        <div className="crew-checkin-active" data-testid="crew-active-checkin">
          <div className="crew-checkin-info">
            <span className="crew-checkin-dot"></span>
            <strong>Checked in:</strong> {activeCheckin.project_address}
            <span className="crew-checkin-time">Since {new Date(activeCheckin.check_in_time).toLocaleTimeString()}</span>
          </div>
          <button onClick={handleCheckOut} className="crew-checkout-btn" data-testid="crew-checkout-btn">Check Out</button>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="crew-empty">No projects assigned yet. Admin will assign you to projects from the Dashboard.</div>
      ) : (
        <div className="crew-project-list" data-testid="crew-project-list">
          {projects.map(p => (
            <div key={p.project_id} className="crew-project-card-wrap">
              <button className="crew-project-card" onClick={() => setSelected(p.project_id)} data-testid={`crew-project-${p.project_id}`}>
                <div className="crew-card-top">
                  <strong>{p.address}{p.unit_number ? ` #${p.unit_number}` : ""}</strong>
                  <span className={`crew-phase-badge phase-${p.phase || "intake"}`}>{p.phase || "Intake"}</span>
                </div>
                <div className="crew-card-meta">
                  <span>{p.city || "DFW"}</span>
                  <span>{p.status}</span>
                </div>
              </button>
              {!activeCheckin && (
                <button onClick={() => handleCheckIn(p)} disabled={checkingIn} className="crew-checkin-btn" data-testid={`checkin-${p.project_id}`}>
                  {checkingIn ? "..." : "Check In"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recent Check-Ins */}
      {checkins.length > 0 && (
        <div className="crew-checkin-history" data-testid="crew-checkin-history">
          <h3>Recent Check-Ins</h3>
          {checkins.slice(0, 10).map(c => {
            let duration = "";
            if (c.check_in_time && c.check_out_time) {
              const mins = Math.round((new Date(c.check_out_time) - new Date(c.check_in_time)) / 60000);
              const hrs = Math.floor(mins / 60);
              const rm = mins % 60;
              duration = hrs > 0 ? `${hrs}h ${rm}m` : `${rm}m`;
            }
            return (
              <div key={c.checkin_id} className="crew-checkin-row">
                <span>{c.project_address}</span>
                <span>{new Date(c.check_in_time).toLocaleDateString()}</span>
                <span>In: {new Date(c.check_in_time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                <span>{c.check_out_time ? `Out: ${new Date(c.check_out_time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` : "On Site"}</span>
                {duration && <span className="crew-checkin-duration">{duration}</span>}
                <span className={`crew-checkin-status-${c.status}`}>{c.status === "on_site" ? "Active" : "Done"}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CrewPortal;
